import React, { useEffect, useMemo } from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { viewGet } from '@uncinc/react-drupal-core/dist/redux/actions/Entity/View';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { APP_OPTIONS, TOPICAL_PATH } from '../../../config/constants';
import { createSearchFacetUrl } from '../../../helpers/createSearchUrl';
import useTypedDispatch from '../../../helpers/useDispatch';
import type { RelatedContentProps } from '../types';
import RelatedContent from '..';

const RelatedContentDefault: React.FC<RelatedContentProps> = ({
  type = 'by_theme',
  args,
  filters,
  className,
  ...restProps
}) => {
  const articleEntities = useSelector<Immutable.Map<any, any>>(
    (state) => state.getIn(['view', 'article', type, 'entities'], Immutable.List()),
  ) as Immutable.List<any> || Immutable.List();
  const eventEntities = useSelector<Immutable.Map<any, any>>(
    (state) => state.getIn(['view', 'events', type, 'entities'], Immutable.List()),
  ) as Immutable.List<any> || Immutable.List();
  const relatedTerm = useSelector<ImmutableRootState<string>, string | undefined>(
    (state) => state.getIn(['taxonomyTerm', String(args[1]), 'name', 0, 'value']),
  );

  const dispatch = useTypedDispatch();
  const { t } = useTranslation();

  // Fetch the views before rendering to check if there are any results at all.
  useEffect(() => {
    if (articleEntities.size === 0) {
      dispatch(viewGet('article', type, 0, args, filters));
    }

    if (eventEntities.size === 0) {
      dispatch(viewGet('events', type, 0, args, filters));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchUrl = useMemo(() => {
    if (!relatedTerm) {
      return TOPICAL_PATH;
    }

    return createSearchFacetUrl({
      pathname: TOPICAL_PATH,
      index: APP_OPTIONS.parameters.searchkitIndexTopical,
      facetType: 'refinementList',
      attribute: type === 'by_theme' ? 'theme' : 'targetAudience',
      value: relatedTerm,
    });
  }, [relatedTerm, type]);

  // If there are no results in both views, return nothing
  if (articleEntities.size === 0 && eventEntities.size === 0) {
    return null;
  }

  return (
    <RelatedContent
      {...restProps}
      className={type === 'by_projects' ? '' : 'mt-0'}
    >
      <RelatedContent.Title
        title={type === 'by_projects' ? t('components.related_content.title_project') : t('components.related_content.title')}
      />

      <div className="row">
        {articleEntities.size > 0 && (
          <div className={type === 'by_projects' ? 'col-lg-12' : 'col-lg-6'}>
            <RelatedContent.RelatedArticles display={type} args={args} filters={filters} />
          </div>
        )}
        {eventEntities.size > 0 && type !== 'by_projects' && (
          <div className="col-lg-6">
            <RelatedContent.RelatedEvents display={type} args={args} filters={filters} />
          </div>
        )}
      </div>

      <RelatedContent.MoreButton
        url={type === 'by_projects' ? TOPICAL_PATH : searchUrl}
      />
    </RelatedContent>
  );
};

export default RelatedContentDefault;
